import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _get from "lodash/get";
import isNil from "lodash/isNil";
import _pick from "lodash/pick";
import React from "react";
import { FormControl, FormGroup } from "react-bootstrap";
import { connect } from "react-redux";
import ReactTooltip from "react-tooltip";
import { trackEvent } from "../../modules/tracker/actions";

import Config from "../../config";
import * as colors from "../../helpers/constants/colors";
import { PAGE_LABELS } from "../../modules/pages/constants";
import * as results from "../../modules/results";
import { ResultsEvents } from "../../modules/tracker/event-names";
import styles from "../../styles/components/ComparePages.css";
import SegmentedBar from "../SegmentedBar/SegmentedBar";
import ToggleButton from "../ToggleButton";
import CompareDriversGraph, {
    CompareDriversGraphLegend
} from "./CompareDriversGraph";
import CompareDriversTable, {
    CompareDriversTableLegend
} from "./CompareDriversTable";
import CrappyPaletteEditor from "./CrappyPaletteEditor";
import TogglePageCardList from "./TogglePageCardList";
import { range } from "lodash";

const DRIVERS = ["Credibility", "Appeal", "Relevance", "Clarity", "Experience"];
const MINIMUM_PAGES_IN_BENCHMARK = 10;
const BENCHMARK_SETS = ["product", "industry", "global"];

function BaselineSelect({ pages, selectedPageId, onChangeBaseline }) {
    const onChange = e => onChangeBaseline(e.target.value);
    return (
        <FormGroup
            controlId="baselineSelect"
            className={styles.baselineSelectFormGroup}
        >
            <p className={styles.key}>Baseline: </p>
            <FormControl
                onChange={onChange}
                componentClass="select"
                value={selectedPageId}
            >
                {pages.map(page => (
                    <option key={`select-option-${page.id}`} value={page.id}>
                        Page {page.pageLabel}: {page.name}
                    </option>
                ))}
            </FormControl>
        </FormGroup>
    );
}

const binColors = colors.segmentedBar.spectrum;
const positiveIndex = 3;
const negativeIndex = 1;
const colorKey = [
    {
        color: binColors[positiveIndex],
        text: "Predicted conversion more than baseline."
    },
    {
        color: "#e6e6e6",
        text: "Too similar to predict."
    },
    {
        color: binColors[negativeIndex],
        text: "Predicted conversion less than baseline."
    }
];

function KeyWithTooltip() {
    return (
        <div>
            <p data-tip data-for="compare-legend" className={styles.key}>
                Key <FontAwesomeIcon icon="info-circle" />
            </p>
            <ReactTooltip
                id="compare-legend"
                place="bottom"
                border={true}
                type="light"
                effect="solid"
            >
                <div className={styles.keyTooltip}>
                    {colorKey.map(item => (
                        <div key={item.color} className={styles.keyRow}>
                            <div
                                className={styles.bar}
                                style={{ background: item.color }}
                            />
                            <span className={styles.label}>{item.text}</span>
                        </div>
                    ))}
                    <div className={styles.keyRow}>
                        <div
                            className={styles.letterIcon}
                            style={{ background: colors.competitorColor }}
                        >
                            <span className={styles.letter}>C</span>
                        </div>
                        <span className={styles.label}>Competitor page</span>
                    </div>
                </div>
            </ReactTooltip>
        </div>
    );
}

function ComparePagesTitle() {
    return (
        <div className={styles.chapterHeader}>
            <h2 className={styles.chapterTitle}>Compare Pages</h2>
        </div>
    );
}

class ComparePages extends React.PureComponent {
    constructor(props) {
        super(props);

        this.onChangeBaseline = this.onChangeBaseline.bind(this);
        this.onChangeSelectedPage = this.onChangeSelectedPage.bind(this);
        this.state = { showDebug: false };
    }

    componentDidMount() {
        trackEvent(ResultsEvents.DID_NAVIGATE_TO_COMPARE_PAGES);
        // on initial mount, all pages are selected
        this.props.setSelectedPages(this.props.pages.map(({ id }) => id));
        this.props.setShowGraph(true);
    }

    componentDidUpdate(prevProps) {
        if (this.props.wevoId !== prevProps.wevoId) {
            // when we switch wevos, we select all pages on the new wevo
            this.props.setSelectedPages(this.props.pages.map(({ id }) => id));
        }
    }

    onChangeBaseline(pageId) {
        const { selectedBaseline, pages } = this.props;
        const newBaseline = pages.find(({ id }) => id === pageId);

        if (selectedBaseline && newBaseline) {
            trackEvent(ResultsEvents.DID_CHANGE_BASELINE_PAGE, {
                from_page: {
                    id: selectedBaseline.id,
                    name: selectedBaseline.name,
                    is_competitor: selectedBaseline.isCompetitor,
                    is_winner: newBaseline.isWinner
                },
                to_page: {
                    id: newBaseline.id,
                    name: newBaseline.name,
                    is_competitor: newBaseline.isCompetitor,
                    is_winner: newBaseline.isWinner
                }
            });
        }
        this.props.changeBaseline(pageId);
    }

    onChangeSelectedPage(selectedPageId) {
        this.props.selectPage(selectedPageId);
    }

    handleTogglePage = ({ id }) => {
        this.props.togglePageSelection(id);
    };

    handleShowGraph = () => {
        this.props.setShowGraph(true);
    };

    handleShowTable = () => {
        this.props.setShowGraph(false);
    };

    render() {
        if (!this.props.hasPageScores) {
            return (
                <div className={styles.chapter}>
                    <ComparePagesTitle />
                    <div>Some pages in this test have not been scored yet.</div>
                </div>
            );
        }

        const { pages, bins, teamId, driverBenchmarks, showGraph } = this.props;
        const baselinePage = pages.find(({ isBaseline }) => isBaseline);
        const selectedPages = pages.filter(({ isSelected }) => isSelected);

        return (
            <div className={styles.chapter}>
                <ComparePagesTitle />

                <div className={styles.horseraceControls}>
                    <KeyWithTooltip />

                    <BaselineSelect
                        pages={pages}
                        selectedPageId={baselinePage.id}
                        onChangeBaseline={this.onChangeBaseline}
                    />
                </div>

                <div className={styles.horserace}>
                    <SegmentedBar pages={selectedPages} bins={bins} />
                </div>

                <div className={styles.pages}>
                    <TogglePageCardList
                        pages={pages}
                        onTogglePage={this.handleTogglePage}
                    />
                </div>

                <div className={styles.subchapterHeader}>
                    <h3 className={styles.chapterTitle}>Driver Scores</h3>
                </div>

                <div className={styles.driverScoresHeader}>
                    <div className={styles.graphTableToggle}>
                        <ToggleButton
                            className={styles.toggle}
                            label="Graph"
                            isSelected={showGraph}
                            onClick={this.handleShowGraph}
                        />
                        <ToggleButton
                            className={styles.toggle}
                            label="Table"
                            isSelected={!showGraph}
                            onClick={this.handleShowTable}
                        />
                    </div>

                    <div className={styles.legendContainer}>
                        {showGraph ? (
                            <CompareDriversGraphLegend />
                        ) : (
                            <CompareDriversTableLegend />
                        )}
                    </div>
                </div>

                <div className={styles.graphTable}>
                    {showGraph ? (
                        <CompareDriversGraph
                            pages={selectedPages}
                            driverBenchmarks={driverBenchmarks}
                            teamId={teamId}
                        />
                    ) : (
                        <CompareDriversTable
                            benchmarks={driverBenchmarks}
                            pages={selectedPages}
                            teamId={teamId}
                            drivers={DRIVERS}
                        />
                    )}
                </div>

                {Config.general().enableComparePagesPaletteEditor && (
                    <a
                        href="#"
                        style={{
                            position: "fixed",
                            right: "2rem",
                            bottom: 0,
                            fontSize: "1.4rem"
                        }}
                        onClick={e => {
                            e.preventDefault();
                            this.setState(state => ({
                                showDebug: !state.showDebug
                            }));
                        }}
                    >
                        &pi;
                    </a>
                )}
                {Config.general().enableComparePagesPaletteEditor &&
                    this.state.showDebug && (
                        <CrappyPaletteEditor
                            {...this.props}
                            onClose={() => {
                                this.setState({ showDebug: false });
                            }}
                        />
                    )}
            </div>
        );
    }
}

export function computeLiftScoresForBaseline(baselinePageId, pages, scores) {
    const baselineScore = scores[baselinePageId].score;
    return pages.reduce((acc, page) => {
        acc[page.id] =
            (100 * (scores[page.id].score - baselineScore)) / baselineScore;
        return acc;
    }, {});
}

function findBaselineSelectedByUser(pages) {
    // Find the page that was selected as baseline by the user.
    const pageSelectedAsBaseLine = pages.find(page => page.isBaseline);
    if (pageSelectedAsBaseLine) {
        return pageSelectedAsBaseLine.id;
    }
}

export function findLowestScorePageId(pages, scores) {
    // Find the page with the lowest raw score. That's our default baseline page.
    return pages
        .map(page => page.id)
        .reduce(
            (baselineId, id) =>
                scores[baselineId].score <= scores[id].score ? baselineId : id
        );
}

export function assignPagesToBins(bins, pageIds, liftScoresByPageId) {
    return bins.map(bin => {
        return {
            ...bin,
            pageIds: pageIds.filter(
                pageId =>
                    (isNil(bin.min) || liftScoresByPageId[pageId] >= bin.min) &&
                    (isNil(bin.max) || liftScoresByPageId[pageId] < bin.max)
            )
        };
    });
}

export function getDriverBenchmarks(benchmarkSets) {
    let last = null;
    for (let set of BENCHMARK_SETS) {
        const benchmarkSet = _get(benchmarkSets, set);
        const driverBenchmarks = _pick(benchmarkSet, DRIVERS);
        const isValid = Object.values(driverBenchmarks).every(benchmark => {
            return benchmark.numPages >= MINIMUM_PAGES_IN_BENCHMARK;
        });
        if (isValid) {
            return driverBenchmarks;
        }
        last = benchmarkSet ? benchmarkSet : last;
    }
    return last;
}

function mapStateToProps(state, props) {
    const { wevo, results } = props;
    let pages = wevo && wevo.pages;

    // Make sure we have a score for each page. If we don't... uh, kick the can down the road I guess
    const hasScores =
        !isNil(pages) &&
        pages.every(page => {
            const scores = _get(results, ["scores", page.id], []);
            return scores !== [];
        });
    if (!hasScores) {
        console.error("Invalid results... did not get a score for every page", {
            pages: pages,
            scores: results.scores
        });
        return {
            hasPageScores: false
        };
    }

    const driverBenchmarks = getDriverBenchmarks(_get(results, "benchmarks"));

    // Decide on a baseline page. If the user selected one, then this is easy. Otherwise we want to
    // select the one whose challengers have the lowest cumulative lift score.
    const baselinePageId =
        parseInt(state.queryString.baseline, 10) ||
        (findBaselineSelectedByUser(pages) ||
            findLowestScorePageId(pages, results.pages));

    // Compute lift scores over the baseline by page ID
    const liftScoresByPageId = computeLiftScoresForBaseline(
        baselinePageId,
        pages,
        results.pages
    );

    // Sort the pages by score and assign them to bins
    const sortedPageIds = pages
        .map(page => page.id)
        .sort(
            (pageId1, pageId2) =>
                liftScoresByPageId[pageId1] - liftScoresByPageId[pageId2]
        );
    const binAssignments = assignPagesToBins(
        results.binConfiguration,
        sortedPageIds,
        liftScoresByPageId
    );

    // Determine the winner. A page is a winner if:
    // a) it's alone in its bin... if there's more than 1 page in the bin, then none are labeled
    // winners
    // b) it's in a bin with a lower bound
    // c) if the bin has an upper bound, the upper bound is a positive number
    // Note: I copy binAssignments because reverse() works in-place and I don't want to modify
    // binAssignments
    const highestPopulatedBin = [...binAssignments]
        .reverse()
        .find(bin => bin.pageIds.length > 0);
    const winnerPageId =
        highestPopulatedBin.pageIds.length === 1 && // a)
        !isNil(highestPopulatedBin.min) && // b)
        (isNil(highestPopulatedBin.max) || highestPopulatedBin.max > 0) && // c)
        highestPopulatedBin.pageIds[0];

    // Cram all of this info into the page objects
    pages = pages.map((page, index) => {
        const pageLabel = PAGE_LABELS[index];
        const color = state.pages.pageColors[index];
        const drivers = _get(results, ["pages", page.id, "drivers"], {});
        const binIndex = binAssignments.findIndex(bin => {
            return bin.pageIds.includes(page.id);
        });
        return {
            ...page,
            pageLabel,
            color,
            drivers,
            rawScore: page.score,
            score: liftScoresByPageId[page.id],
            isBaseline: page.id === baselinePageId,
            isWinner: page.id === winnerPageId,
            isSelected: state.results.selectedPageIds.includes(page.id),
            binIndex: binIndex
        };
    });
    pages = pages.sort((a, b) => a.pageLabel.localeCompare(b.pageLabel));

    return {
        hasPageScores: true,
        bins: binAssignments,
        driverBenchmarks,
        pages,
        wevoId: wevo.id,
        showGraph: state.results.showGraph
    };
}

function mapDispatchToProps(dispatch) {
    return {
        selectPage: pageId => dispatch(results.selectPage(pageId)),
        changeBaseline: pageId => dispatch(results.changeBaseline(pageId)),
        setSelectedPages: pageIds =>
            dispatch(results.setSelectedPages(pageIds)),
        setShowGraph: showGraph =>
            dispatch(results.setShowGraphOnComparePagesScreen(showGraph)),
        togglePageSelection: pageId =>
            dispatch(results.togglePageSelection(pageId))
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ComparePages);



// WEBPACK FOOTER //
// ./src/components/Results/ComparePages.js